import { useCallback, useMemo } from 'react'

const EMPTY_ANCESTORS: readonly string[] = []
const noAncestors = (): readonly string[] => EMPTY_ANCESTORS

interface IUseGroupExpansionOptions {
  groupKeys: string[]
  collapsedKeys: Set<string>
  onCollapsedKeysChange: (keys: Set<string>) => void
  /** Returns ancestor keys to enable descendant-cascade collapse. */
  getGroupAncestors?: (key: string) => readonly string[]
}

export default function useGroupExpansion({
  groupKeys,
  collapsedKeys,
  onCollapsedKeysChange,
  getGroupAncestors = noAncestors,
}: IUseGroupExpansionOptions) {
  // Preserve reference identity when groupKeys is already unique (memo stability).
  const uniqueGroupKeys = useMemo(() => {
    const unique = new Set(groupKeys)
    return unique.size === groupKeys.length ? groupKeys : Array.from(unique)
  }, [groupKeys])

  const visibleKeys = useMemo(
    () =>
      uniqueGroupKeys.filter(
        key =>
          !getGroupAncestors(key).some(ancestor => collapsedKeys.has(ancestor)),
      ),
    [uniqueGroupKeys, getGroupAncestors, collapsedKeys],
  )

  const isCollapsed = useCallback(
    (key: string) =>
      collapsedKeys.has(key) ||
      getGroupAncestors(key).some(ancestor => collapsedKeys.has(ancestor)),
    [collapsedKeys, getGroupAncestors],
  )

  const toggleGroup = useCallback(
    (key: string) => {
      const next = new Set(collapsedKeys)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      onCollapsedKeysChange(next)
    },
    [collapsedKeys, onCollapsedKeysChange],
  )

  const collapseAll = useCallback(() => {
    // No-op when every visible key is already collapsed.
    if (
      visibleKeys.length > 0 &&
      visibleKeys.every(key => collapsedKeys.has(key))
    ) {
      return
    }
    // Preserve stale collapsed entries hidden under a collapsed ancestor;
    // wholesale replacement would surface descendants in the wrong state.
    const next = new Set(collapsedKeys)
    visibleKeys.forEach(key => next.add(key))
    onCollapsedKeysChange(next)
  }, [visibleKeys, collapsedKeys, onCollapsedKeysChange])

  const expandAll = useCallback(() => {
    // No-op when no visible key is currently collapsed.
    if (!visibleKeys.some(key => collapsedKeys.has(key))) return
    const next = new Set(collapsedKeys)
    visibleKeys.forEach(key => next.delete(key))
    onCollapsedKeysChange(next)
  }, [collapsedKeys, visibleKeys, onCollapsedKeysChange])

  const isAllCollapsed = useMemo(() => {
    if (visibleKeys.length === 0) return false
    return visibleKeys.every(key => collapsedKeys.has(key))
  }, [visibleKeys, collapsedKeys])

  return {
    collapsedKeys,
    isCollapsed,
    toggleGroup,
    collapseAll,
    expandAll,
    isAllCollapsed,
  }
}
